import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ScrollView, ScrollViewProps } from 'react-native';

interface StableScrollViewProps extends ScrollViewProps {
  children: React.ReactNode;
  preventAutoScroll?: boolean;
  debounceMs?: number;
}

/**
 * A ScrollView component that prevents unwanted scroll position changes
 * during content updates, helping to avoid Safari race conditions.
 */
export const StableScrollView: React.FC<StableScrollViewProps> = ({
  children,
  preventAutoScroll = true,
  debounceMs = 100,
  ...props
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const lastScrollYRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track scroll position to prevent unwanted changes
  const handleScroll = useCallback((event: any) => {
    if (props.onScroll) {
      props.onScroll(event);
    }
    
    const scrollY = event.nativeEvent.contentOffset.y;
    lastScrollYRef.current = scrollY;
    
    // Mark as scrolling
    setIsScrolling(true);
    
    // Clear previous timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set not scrolling after debounce period
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, debounceMs);
  }, [props.onScroll, debounceMs]);

  // Handle content size changes
  const handleContentSizeChange = useCallback((width: number, height: number) => {
    if (props.onContentSizeChange) {
      props.onContentSizeChange(width, height);
    }
    
    // Only update if content height actually changed
    if (height !== contentHeight) {
      setContentHeight(height);
      
      // If we're not actively scrolling and preventAutoScroll is enabled,
      // maintain the current scroll position
      if (!isScrolling && preventAutoScroll && scrollViewRef.current) {
        const currentScrollY = lastScrollYRef.current;
        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          scrollViewRef.current?.scrollTo({
            y: currentScrollY,
            animated: false
          });
        });
      }
    }
  }, [props.onContentSizeChange, contentHeight, isScrolling, preventAutoScroll]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ScrollView
      ref={scrollViewRef}
      {...props}
      onScroll={handleScroll}
      onContentSizeChange={handleContentSizeChange}
      scrollEventThrottle={16}
      showsVerticalScrollIndicator={props.showsVerticalScrollIndicator ?? false}
    >
      {children}
    </ScrollView>
  );
};

export default StableScrollView; 